import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Activity, TrendingUp, TrendingDown, RefreshCw, Loader,
  Search, Calendar, Info, BarChart3, Layers,
  ArrowUpRight, ArrowDownRight, Clock, Target, BookOpen,
  HelpCircle, X, Lightbulb, MousePointer, Eye
} from 'lucide-react';

/**
 * Historical GEX Chart - Mobile Friendly
 * Shows gamma exposure changes over time with interactive tooltips
 */

const HistoricalGEXChart = ({ symbol: initialSymbol }) => {
  const [currentSymbol, setCurrentSymbol] = useState(initialSymbol || 'SPY');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [timeRange, setTimeRange] = useState('1M');
  const [showPrice, setShowPrice] = useState(true);
  const [showFlipLevel, setShowFlipLevel] = useState(true);
  const [showEducation, setShowEducation] = useState(false);
  
  // Mobile detection and hover state
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  
  const chartRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Update currentSymbol when prop changes
  useEffect(() => {
    if (initialSymbol && initialSymbol !== currentSymbol) {
      setCurrentSymbol(initialSymbol);
    }
  }, [initialSymbol]);

  useEffect(() => {
    if (currentSymbol) {
      fetchHistoricalGEX();
    }
  }, [currentSymbol, timeRange]);

  const fetchHistoricalGEX = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/gamma/history/${currentSymbol}?range=${timeRange}`);
      const data = await response.json();
      
      if (data.success && data.history) {
        setHistoryData(data);
      } else {
        setHistoryData(generateDemoHistory(currentSymbol, timeRange));
      }
    } catch (err) {
      console.error('Historical GEX error:', err);
      setHistoryData(generateDemoHistory(currentSymbol, timeRange));
    } finally {
      setLoading(false);
    }
  };

  const generateDemoHistory = (symbol, range) => {
    const days = { '1W': 7, '1M': 30, '3M': 90, '6M': 180, '1Y': 365 }[range] || 30;
    const history = [];
    
    let price = symbol === 'SPY' ? 450 : symbol === 'QQQ' ? 380 : 150;
    let gex = Math.random() * 2e9 - 1e9;
    let flipLevel = price * (0.98 + Math.random() * 0.04);
    
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      price = price * (1 + (Math.random() - 0.5) * 0.02);
      gex = gex + (Math.random() - 0.5) * 5e8;
      gex = Math.max(-3e9, Math.min(3e9, gex));
      flipLevel = flipLevel * (1 + (Math.random() - 0.5) * 0.005);
      
      history.push({
        date: date.toISOString().split('T')[0],
        price: price,
        netGEX: gex,
        flipLevel: flipLevel,
        gammaEnvironment: price > flipLevel ? 'POSITIVE' : 'NEGATIVE'
      });
    }
    
    return {
      success: true,
      symbol,
      history,
      summary: {
        avgGEX: history.reduce((sum, h) => sum + h.netGEX, 0) / history.length,
        maxGEX: Math.max(...history.map(h => h.netGEX)),
        minGEX: Math.min(...history.map(h => h.netGEX)),
        currentGEX: history[history.length - 1]?.netGEX || 0,
        gexTrend: history[history.length - 1]?.netGEX > history[history.length - 8]?.netGEX ? 'up' : 'down',
        daysPositive: history.filter(h => h.gammaEnvironment === 'POSITIVE').length,
        daysNegative: history.filter(h => h.gammaEnvironment === 'NEGATIVE').length
      }
    };
  };

  const searchStocks = async (query) => {
    if (query.length < 1) { setSearchResults([]); return; }
    try {
      const response = await fetch(`${API_URL}/stocks/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setSearchResults(data || []);
      setShowSearchResults(true);
    } catch (err) { setSearchResults([]); }
  };

  const selectStock = (symbol) => {
    setCurrentSymbol(symbol);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const formatGEX = (value) => {
    if (!value) return '0';
    const absValue = Math.abs(value);
    if (absValue >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
    if (absValue >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (absValue >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
    return value.toFixed(0);
  };

  const chartData = useMemo(() => {
    if (!historyData?.history) return null;
    const history = historyData.history;
    const prices = history.map(h => h.price);
    const gexValues = history.map(h => h.netGEX);
    
    return {
      history,
      minPrice: Math.min(...prices),
      maxPrice: Math.max(...prices),
      minGEX: Math.min(...gexValues),
      maxGEX: Math.max(...gexValues),
      priceRange: Math.max(...prices) - Math.min(...prices)
    };
  }, [historyData]);

  // Handle chart interactions
  const handleChartInteraction = (index, event) => {
    if (chartData && chartData.history[index]) {
      if (isMobile) {
        setHoveredPoint(hoveredPoint?.index === index ? null : { ...chartData.history[index], index });
      } else {
        setHoveredPoint({ ...chartData.history[index], index });
      }
    }
  };

  if (loading && !historyData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        padding: isMobile ? '2rem 1rem' : '4rem', 
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)', 
        borderRadius: '1rem', 
        minHeight: '500px' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={48} color="#a78bfa" className="spin" />
          <p style={{ color: '#a78bfa', marginTop: '1rem' }}>Loading Historical GEX...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
      borderRadius: isMobile ? '0.75rem' : '1rem',
      overflow: 'hidden',
      border: '1px solid rgba(139, 92, 246, 0.2)'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
        borderBottom: '1px solid rgba(16, 185, 129, 0.2)',
        padding: isMobile ? '1rem' : '1.5rem 2rem'
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: isMobile ? '40px' : '48px',
              height: isMobile ? '40px' : '48px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)'
            }}>
              <Activity size={isMobile ? 22 : 28} color="white" />
            </div>
            <div>
              <h2 style={{ 
                fontSize: isMobile ? '1.25rem' : '1.5rem', 
                fontWeight: '700', 
                margin: 0,
                color: '#e2e8f0'
              }}>
                Historical GEX
              </h2>
              <p style={{ color: '#94a3b8', fontSize: isMobile ? '0.75rem' : '0.875rem', margin: 0 }}>
                Gamma Exposure Over Time
              </p>
            </div>
          </div>
          
          {/* Current Symbol Badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'rgba(59, 130, 246, 0.2)',
            border: '1px solid rgba(59, 130, 246, 0.4)',
            borderRadius: '0.5rem',
            marginLeft: isMobile ? 0 : '1rem',
            marginTop: isMobile ? '0.5rem' : 0
          }}>
            <BarChart3 size={18} color="#60a5fa" />
            <span style={{ 
              color: '#60a5fa', 
              fontWeight: '700', 
              fontSize: isMobile ? '1rem' : '1.1rem' 
            }}>
              {currentSymbol}
            </span>
          </div>
          
          {/* Time Range & Actions */}
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            flexWrap: 'wrap',
            alignItems: 'center',
            marginLeft: isMobile ? 0 : 'auto'
          }}>
            {/* Time Range Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '0.25rem', 
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '0.5rem',
              padding: '0.25rem'
            }}>
              {['1W', '1M', '3M', '6M', '1Y'].map(range => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  style={{
                    padding: isMobile ? '0.375rem 0.5rem' : '0.5rem 0.75rem',
                    background: timeRange === range ? 'rgba(16, 185, 129, 0.3)' : 'transparent',
                    border: timeRange === range ? '1px solid rgba(16, 185, 129, 0.5)' : '1px solid transparent',
                    borderRadius: '0.375rem',
                    color: timeRange === range ? '#10b981' : '#94a3b8',
                    cursor: 'pointer',
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  {range}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setShowEducation(!showEducation)}
              style={{
                padding: isMobile ? '0.5rem' : '0.625rem 1rem',
                background: showEducation ? 'rgba(251, 191, 36, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: `1px solid ${showEducation ? 'rgba(251, 191, 36, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
                borderRadius: '0.5rem',
                color: showEducation ? '#fbbf24' : '#94a3b8',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
            >
              <HelpCircle size={16} />
              {!isMobile && 'Learn'}
            </button>
            
            <button 
              onClick={fetchHistoricalGEX} 
              disabled={loading} 
              style={{ 
                padding: isMobile ? '0.5rem' : '0.625rem 1rem', 
                background: 'rgba(16, 185, 129, 0.2)', 
                border: '1px solid rgba(16, 185, 129, 0.4)', 
                borderRadius: '0.5rem', 
                color: '#10b981', 
                cursor: loading ? 'not-allowed' : 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                fontSize: '0.8rem', 
                fontWeight: '600' 
              }}
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
              {!isMobile && 'Refresh'}
            </button>
          </div>
        </div>

        {/* Search */}
        <div style={{ marginTop: '1rem', position: 'relative', maxWidth: isMobile ? '100%' : '400px' }}>
          <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
          <input 
            type="text" 
            value={searchQuery} 
            onChange={(e) => { setSearchQuery(e.target.value); searchStocks(e.target.value); }} 
            onFocus={() => setShowSearchResults(searchResults.length > 0)}
            placeholder={`Search symbol... (current: ${currentSymbol})`} 
            style={{ 
              width: '100%', 
              padding: '0.75rem 1rem 0.75rem 2.75rem', 
              background: 'rgba(15, 23, 42, 0.8)', 
              border: '1px solid rgba(100, 116, 139, 0.3)', 
              borderRadius: '0.5rem', 
              color: 'white', 
              fontSize: '0.9rem', 
              outline: 'none' 
            }} 
          />
          {showSearchResults && searchResults.length > 0 && (
            <div style={{ 
              position: 'absolute', 
              top: '100%', 
              left: 0, 
              right: 0, 
              marginTop: '0.5rem', 
              background: '#1e293b', 
              border: '1px solid #334155', 
              borderRadius: '0.5rem', 
              maxHeight: '200px', 
              overflowY: 'auto', 
              zIndex: 100 
            }}>
              {searchResults.map((result, idx) => (
                <div 
                  key={idx} 
                  onClick={() => selectStock(result.symbol)} 
                  style={{ padding: '0.75rem 1rem', cursor: 'pointer' }}
                >
                  <div style={{ fontWeight: '600', color: '#60a5fa' }}>{result.symbol}</div>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{result.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Education Panel */}
      {showEducation && (
        <HistoricalGEXEducation onClose={() => setShowEducation(false)} isMobile={isMobile} />
      )}

      {/* Backfill/Limited Data Banner */}
      {historyData && historyData.history && historyData.history.length < 10 && (
        <div style={{
          margin: isMobile ? '1rem' : '1rem 2rem',
          padding: '1rem 1.25rem',
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '0.75rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem'
        }}>
          <div style={{
            background: 'rgba(251, 191, 36, 0.2)',
            borderRadius: '50%',
            padding: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Clock size={20} color="#fbbf24" />
          </div>
          <div>
            <div style={{ 
              color: '#fbbf24', 
              fontWeight: '600', 
              fontSize: '0.95rem',
              marginBottom: '0.25rem'
            }}>
              📊 Loading Historical Data for {currentSymbol}
            </div>
            <div style={{ 
              color: '#94a3b8', 
              fontSize: '0.85rem',
              lineHeight: '1.5'
            }}>
              We're fetching historical GEX data in the background. This typically takes 1-2 minutes. 
              <strong style={{ color: '#60a5fa' }}> Refresh the page</strong> in a moment to see the full history!
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {historyData && chartData && (
        <div style={{ padding: isMobile ? '1rem' : '1.5rem 2rem' }}>
          {/* Stats Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(140px, 1fr))', 
            gap: isMobile ? '0.75rem' : '1rem', 
            marginBottom: '1.5rem' 
          }}>
            <StatCard 
              label="Current GEX" 
              value={formatGEX(historyData.summary?.currentGEX)} 
              color={historyData.summary?.currentGEX > 0 ? '#10b981' : '#ef4444'} 
              trend={historyData.summary?.gexTrend}
              isMobile={isMobile}
              tooltip="Current net gamma exposure. Positive = bullish dealer positioning."
            />
            <StatCard 
              label="Avg GEX" 
              value={formatGEX(historyData.summary?.avgGEX)} 
              color="#60a5fa"
              isMobile={isMobile}
              tooltip="Average GEX over the selected time period."
            />
            <StatCard 
              label="Max GEX" 
              value={formatGEX(historyData.summary?.maxGEX)} 
              color="#10b981"
              isMobile={isMobile}
              tooltip="Peak bullish gamma positioning in this period."
            />
            <StatCard 
              label="Min GEX" 
              value={formatGEX(historyData.summary?.minGEX)} 
              color="#ef4444"
              isMobile={isMobile}
              tooltip="Peak bearish gamma positioning in this period."
            />
            <StatCard 
              label="Days Positive γ" 
              value={`${historyData.summary?.daysPositive || 0}`} 
              color="#10b981" 
              subtitle="Stable regime"
              isMobile={isMobile}
              tooltip="Days when price was above gamma flip = low volatility environment."
            />
            <StatCard 
              label="Days Negative γ" 
              value={`${historyData.summary?.daysNegative || 0}`} 
              color="#ef4444" 
              subtitle="Volatile regime"
              isMobile={isMobile}
              tooltip="Days when price was below gamma flip = high volatility environment."
            />
          </div>

          {/* Chart Controls */}
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            marginBottom: '1rem', 
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                fontSize: '0.8rem', 
                color: '#94a3b8', 
                cursor: 'pointer' 
              }}>
                <input 
                  type="checkbox" 
                  checked={showPrice} 
                  onChange={(e) => setShowPrice(e.target.checked)} 
                  style={{ accentColor: '#60a5fa' }} 
                /> 
                Show Price
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                fontSize: '0.8rem', 
                color: '#94a3b8', 
                cursor: 'pointer' 
              }}>
                <input 
                  type="checkbox" 
                  checked={showFlipLevel} 
                  onChange={(e) => setShowFlipLevel(e.target.checked)} 
                  style={{ accentColor: '#f59e0b' }} 
                /> 
                Show Flip Level
              </label>
            </div>
            
            {isMobile && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.375rem',
                padding: '0.25rem 0.5rem',
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '0.25rem',
                fontSize: '0.65rem',
                color: '#93c5fd'
              }}>
                <MousePointer size={12} />
                Tap bars for details
              </div>
            )}
          </div>

          {/* Hover Tooltip */}
          {hoveredPoint && (
            <div style={{
              background: 'rgba(15, 23, 42, 0.95)',
              border: '1px solid rgba(139, 92, 246, 0.5)',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              marginBottom: '0.75rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.75rem'
            }}>
              <div>
                <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Date: </span>
                <span style={{ color: '#e2e8f0', fontWeight: '600' }}>
                  {new Date(hoveredPoint.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
              <div>
                <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>GEX: </span>
                <span style={{ color: hoveredPoint.netGEX > 0 ? '#10b981' : '#ef4444', fontWeight: '700' }}>
                  {formatGEX(hoveredPoint.netGEX)}
                </span>
              </div>
              <div>
                <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Price: </span>
                <span style={{ color: '#60a5fa', fontWeight: '600' }}>${hoveredPoint.price.toFixed(2)}</span>
              </div>
              <div>
                <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Regime: </span>
                <span style={{ 
                  color: hoveredPoint.gammaEnvironment === 'POSITIVE' ? '#10b981' : '#ef4444', 
                  fontWeight: '600' 
                }}>
                  {hoveredPoint.gammaEnvironment}
                </span>
              </div>
            </div>
          )}

          {/* Interactive Chart */}
          <div 
            ref={chartRef}
            style={{ 
              background: 'rgba(15, 23, 42, 0.8)', 
              borderRadius: '0.75rem', 
              padding: isMobile ? '0.75rem' : '1.5rem', 
              marginBottom: '1.5rem',
              position: 'relative',
              touchAction: 'pan-y'
            }}
          >
            <svg 
              width="100%" 
              height={isMobile ? 300 : 370} 
              viewBox={`0 0 ${isMobile ? 420 : 920} ${isMobile ? 300 : 370}`} 
              preserveAspectRatio="xMidYMid meet"
              style={{ overflow: 'visible' }}
            >
              {/* Grid lines */}
              <line x1={isMobile ? 50 : 70} y1={isMobile ? 140 : 175} x2={isMobile ? 380 : 870} y2={isMobile ? 140 : 175} stroke="#64748b" strokeWidth="1" strokeDasharray="5,5" />
              <text x={isMobile ? 45 : 65} y={isMobile ? 144 : 179} fill="#64748b" fontSize={isMobile ? 9 : 11} textAnchor="end">0</text>

              {/* GEX bars */}
              {chartData.history.map((point, idx) => {
                const len = chartData.history.length;
                const chartWidth = isMobile ? 320 : 780;
                const chartOffset = isMobile ? 55 : 80;
                // Handle single data point - center it
                const x = len > 1 
                  ? chartOffset + (idx / (len - 1)) * chartWidth 
                  : chartOffset + chartWidth / 2;
                // Wider bars for fewer data points
                const barWidth = len > 1 
                  ? Math.max(4, Math.min(40, chartWidth / len - 2)) 
                  : 60;
                const maxAbs = Math.max(Math.abs(chartData.maxGEX || 1), Math.abs(chartData.minGEX || 1)) || 1;
                const gexHeight = maxAbs > 0 
                  ? Math.abs(point.netGEX || 0) / maxAbs * (isMobile ? 110 : 140) 
                  : 0;
                const isPositive = (point.netGEX || 0) > 0;
                const isHovered = hoveredPoint?.index === idx;
                
                return (
                  <g key={idx}>
                    <rect 
                      x={x - barWidth / 2} 
                      y={isPositive ? (isMobile ? 140 : 175) - gexHeight : (isMobile ? 140 : 175)} 
                      width={barWidth} 
                      height={Math.max(1, gexHeight)} 
                      fill={isPositive 
                        ? (isHovered ? 'rgba(52, 211, 153, 0.9)' : 'rgba(16, 185, 129, 0.7)') 
                        : (isHovered ? 'rgba(248, 113, 113, 0.9)' : 'rgba(239, 68, 68, 0.7)')} 
                      rx={2}
                      style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}
                      onClick={() => handleChartInteraction(idx)}
                      onMouseEnter={() => !isMobile && setHoveredPoint({ ...point, index: idx })}
                      onMouseLeave={() => !isMobile && setHoveredPoint(null)}
                    />
                    {/* Invisible larger hit area for touch */}
                    <rect
                      x={x - barWidth}
                      y={0}
                      width={barWidth * 2}
                      height={isMobile ? 300 : 370}
                      fill="transparent"
                      onClick={() => handleChartInteraction(idx)}
                    />
                  </g>
                );
              })}

              {/* Price line - only show if more than 1 point and valid range */}
              {showPrice && chartData.history.length > 1 && (chartData.priceRange || 0) > 0 && (
                <path 
                  d={chartData.history.map((point, idx) => {
                    const len = chartData.history.length;
                    const chartWidth = isMobile ? 320 : 780;
                    const chartOffset = isMobile ? 55 : 80;
                    const x = len > 1 
                      ? chartOffset + (idx / (len - 1)) * chartWidth 
                      : chartOffset + chartWidth / 2;
                    const chartHeight = isMobile ? 240 : 280;
                    const priceRange = chartData.priceRange || 1;
                    const y = (isMobile ? 270 : 330) - (((point.price || 0) - (chartData.minPrice || 0)) / priceRange) * chartHeight;
                    const safeY = isFinite(y) ? y : (isMobile ? 140 : 175);
                    return `${idx === 0 ? 'M' : 'L'} ${x} ${safeY}`;
                  }).join(' ')} 
                  fill="none" 
                  stroke="#60a5fa" 
                  strokeWidth="2" 
                />
              )}

              {/* Flip level - only show if more than 1 point and valid range */}
              {showFlipLevel && chartData.history.length > 1 && (chartData.priceRange || 0) > 0 && (
                <path 
                  d={chartData.history.map((point, idx) => {
                    const len = chartData.history.length;
                    const chartWidth = isMobile ? 320 : 780;
                    const chartOffset = isMobile ? 55 : 80;
                    const x = len > 1 
                      ? chartOffset + (idx / (len - 1)) * chartWidth 
                      : chartOffset + chartWidth / 2;
                    const chartHeight = isMobile ? 240 : 280;
                    const priceRange = chartData.priceRange || 1;
                    
                    // Use price as fallback if flipLevel is invalid or way out of range
                    let flipValue = point.flipLevel;
                    if (!flipValue || flipValue <= 0 || 
                        flipValue < chartData.minPrice * 0.5 || 
                        flipValue > chartData.maxPrice * 1.5) {
                      flipValue = point.price; // Fallback to price
                    }
                    
                    const y = (isMobile ? 270 : 330) - ((flipValue - (chartData.minPrice || 0)) / priceRange) * chartHeight;
                    
                    // Clamp Y to chart bounds
                    const minY = isMobile ? 30 : 40;
                    const maxY = isMobile ? 270 : 330;
                    const clampedY = Math.max(minY, Math.min(maxY, y));
                    const safeY = isFinite(clampedY) ? clampedY : (isMobile ? 140 : 175);
                    
                    return `${idx === 0 ? 'M' : 'L'} ${x} ${safeY}`;
                  }).join(' ')} 
                  fill="none" 
                  stroke="#f59e0b" 
                  strokeWidth="2" 
                  strokeDasharray="5,5" 
                />
              )}

              {/* Labels - with more padding */}
              <text x={isMobile ? 48 : 68} y={isMobile ? 30 : 40} fill="#10b981" fontSize={isMobile ? 9 : 11} textAnchor="end">
                +{formatGEX(chartData.maxGEX || 0)}
              </text>
              <text x={isMobile ? 48 : 68} y={isMobile ? 260 : 320} fill="#ef4444" fontSize={isMobile ? 9 : 11} textAnchor="end">
                {formatGEX(chartData.minGEX || 0)}
              </text>
              {showPrice && (
                <>
                  <text x={isMobile ? 390 : 885} y={isMobile ? 35 : 45} fill="#60a5fa" fontSize={isMobile ? 9 : 11}>
                    ${(chartData.maxPrice || 0).toFixed(0)}
                  </text>
                  <text x={isMobile ? 390 : 885} y={isMobile ? 265 : 325} fill="#60a5fa" fontSize={isMobile ? 9 : 11}>
                    ${(chartData.minPrice || 0).toFixed(0)}
                  </text>
                </>
              )}

              {/* Legend */}
              <g transform={`translate(${isMobile ? 60 : 90}, ${isMobile ? 280 : 350})`}>
                <rect x={0} y={0} width={12} height={12} fill="rgba(16, 185, 129, 0.7)" rx={2} />
                <text x={16} y={10} fill="#94a3b8" fontSize={isMobile ? 9 : 11}>+GEX</text>
                <rect x={isMobile ? 60 : 85} y={0} width={12} height={12} fill="rgba(239, 68, 68, 0.7)" rx={2} />
                <text x={isMobile ? 76 : 101} y={10} fill="#94a3b8" fontSize={isMobile ? 9 : 11}>-GEX</text>
                {showPrice && (
                  <>
                    <line x1={isMobile ? 120 : 170} y1={6} x2={isMobile ? 138 : 195} y2={6} stroke="#60a5fa" strokeWidth="2" />
                    <text x={isMobile ? 143 : 203} y={10} fill="#94a3b8" fontSize={isMobile ? 9 : 11}>Price</text>
                  </>
                )}
                {showFlipLevel && (
                  <>
                    <line x1={isMobile ? 180 : 260} y1={6} x2={isMobile ? 198 : 285} y2={6} stroke="#f59e0b" strokeWidth="2" strokeDasharray="3,3" />
                    <text x={isMobile ? 203 : 293} y={10} fill="#94a3b8" fontSize={isMobile ? 9 : 11}>Flip</text>
                  </>
                )}
              </g>
            </svg>
          </div>

          {/* Insights Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
            gap: '1rem' 
          }}>
            <div style={{ 
              background: 'rgba(30, 41, 59, 0.5)', 
              border: '1px solid rgba(71, 85, 105, 0.5)', 
              borderRadius: '0.75rem', 
              padding: '1.25rem' 
            }}>
              <h4 style={{ 
                margin: '0 0 1rem 0', 
                fontSize: '0.9rem', 
                fontWeight: '600', 
                color: '#e2e8f0', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem' 
              }}>
                <Activity size={18} color="#a78bfa" /> GEX Trend Analysis
              </h4>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#cbd5e1', lineHeight: '1.6' }}>
                {currentSymbol} spent <strong style={{ color: '#10b981' }}>{historyData.summary?.daysPositive || 0} days</strong> in positive gamma and <strong style={{ color: '#ef4444' }}>{historyData.summary?.daysNegative || 0} days</strong> in negative gamma.
                Current trend: <span style={{ 
                  color: historyData.summary?.gexTrend === 'up' ? '#10b981' : '#ef4444', 
                  fontWeight: '600' 
                }}>
                  {historyData.summary?.gexTrend === 'up' ? 'increasing ↑' : 'decreasing ↓'}
                </span>
              </p>
            </div>
            
            <div style={{ 
              background: 'rgba(30, 41, 59, 0.5)', 
              border: '1px solid rgba(71, 85, 105, 0.5)', 
              borderRadius: '0.75rem', 
              padding: '1.25rem' 
            }}>
              <h4 style={{ 
                margin: '0 0 1rem 0', 
                fontSize: '0.9rem', 
                fontWeight: '600', 
                color: '#e2e8f0', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem' 
              }}>
                <Target size={18} color="#f59e0b" /> Trading Implications
              </h4>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.8rem', color: '#cbd5e1', lineHeight: '1.8' }}>
                <li>{historyData.summary?.currentGEX > 0 ? 'Positive GEX: Favor mean reversion strategies' : 'Negative GEX: Favor momentum strategies'}</li>
                <li>{historyData.summary?.gexTrend === 'up' ? 'Rising GEX = volatility compression ahead' : 'Falling GEX = volatility expansion ahead'}</li>
                <li>Watch for regime changes at the gamma flip level</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// STAT CARD WITH TOOLTIP
// ============================================
const StatCard = ({ label, value, color, trend, subtitle, isMobile, tooltip }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div style={{ 
      background: 'rgba(30, 41, 59, 0.5)', 
      border: '1px solid rgba(71, 85, 105, 0.5)', 
      borderRadius: '0.5rem', 
      padding: isMobile ? '0.75rem' : '1rem',
      position: 'relative'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '0.25rem'
      }}>
        <div style={{ fontSize: isMobile ? '0.65rem' : '0.75rem', color: '#94a3b8' }}>{label}</div>
        {tooltip && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setShowTooltip(!showTooltip);
            }}
            onMouseEnter={() => !isMobile && setShowTooltip(true)}
            onMouseLeave={() => !isMobile && setShowTooltip(false)}
            style={{ cursor: 'pointer' }}
          >
            <Info size={12} color="#64748b" />
            
            {showTooltip && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                right: 0,
                marginBottom: '0.5rem',
                padding: '0.5rem 0.75rem',
                background: '#1e293b',
                border: '1px solid #475569',
                borderRadius: '0.375rem',
                fontSize: '0.7rem',
                color: '#cbd5e1',
                width: '180px',
                zIndex: 100,
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
                lineHeight: '1.4'
              }}>
                {tooltip}
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: isMobile ? '1rem' : '1.25rem', fontWeight: '700', color }}>{value}</span>
        {trend && (
          <span style={{ color }}>
            {trend === 'up' ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
          </span>
        )}
      </div>
      {subtitle && (
        <div style={{ fontSize: isMobile ? '0.6rem' : '0.65rem', color: '#64748b', marginTop: '0.25rem' }}>
          {subtitle}
        </div>
      )}
    </div>
  );
};

// ============================================
// EDUCATION PANEL
// ============================================
const HistoricalGEXEducation = ({ onClose, isMobile }) => (
  <div style={{
    background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.05) 0%, rgba(245, 158, 11, 0.05) 100%)',
    borderBottom: '1px solid rgba(251, 191, 36, 0.2)',
    padding: isMobile ? '1rem' : '1.5rem 2rem'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
      <h3 style={{ 
        fontSize: isMobile ? '1rem' : '1.125rem', 
        fontWeight: '700', 
        color: '#fbbf24',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <BookOpen size={18} />
        Reading Historical GEX
      </h3>
      <button
        onClick={onClose}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#94a3b8',
          cursor: 'pointer',
          padding: '0.25rem'
        }}
      >
        <X size={20} />
      </button>
    </div>

    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: '1rem'
    }}>
      <div style={{
        background: 'rgba(15, 23, 42, 0.5)',
        border: '1px solid rgba(71, 85, 105, 0.3)',
        borderRadius: '0.5rem',
        padding: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>📊</span>
          <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0' }}>
            Reading the Bars
          </h4>
        </div>
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8', lineHeight: '1.6' }}>
          <strong style={{ color: '#10b981' }}>Green bars</strong> = Positive GEX (bullish dealer positioning). 
          <strong style={{ color: '#ef4444' }}> Red bars</strong> = Negative GEX (bearish positioning). 
          Taller bars indicate stronger positioning.
        </p>
      </div>

      <div style={{
        background: 'rgba(15, 23, 42, 0.5)',
        border: '1px solid rgba(71, 85, 105, 0.3)',
        borderRadius: '0.5rem',
        padding: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>📈</span>
          <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0' }}>
            Price vs Flip Line
          </h4>
        </div>
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8', lineHeight: '1.6' }}>
          The <strong style={{ color: '#60a5fa' }}>blue line</strong> shows price history. 
          The <strong style={{ color: '#f59e0b' }}>orange dashed line</strong> is the gamma flip level. 
          When price is above flip = stable regime, below = volatile regime.
        </p>
      </div>

      <div style={{
        background: 'rgba(15, 23, 42, 0.5)',
        border: '1px solid rgba(71, 85, 105, 0.3)',
        borderRadius: '0.5rem',
        padding: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>💡</span>
          <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0' }}>
            Trading Signals
          </h4>
        </div>
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8', lineHeight: '1.6' }}>
          Watch for <strong>regime changes</strong> when price crosses the flip line. 
          Rising GEX often precedes low-volatility periods. 
          Falling GEX often signals incoming volatility.
        </p>
      </div>
    </div>

    <div style={{
      marginTop: '1rem',
      padding: '0.75rem 1rem',
      background: 'rgba(16, 185, 129, 0.1)',
      border: '1px solid rgba(16, 185, 129, 0.3)',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    }}>
      <Lightbulb size={16} color="#10b981" />
      <p style={{ margin: 0, fontSize: '0.8rem', color: '#a7f3d0' }}>
        <strong>Pro Tip:</strong> Look for divergences between price and GEX. Rising price with falling GEX can signal an upcoming reversal.
      </p>
    </div>
  </div>
);

// Add spin animation
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .spin {
    animation: spin 1s linear infinite;
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(style);
}

export default HistoricalGEXChart;